import { usePublishPageCrumb } from '../../store/usePageSettingsStore';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ArrowLeft, Archive, Mail, Phone, Globe, Trash2, User, UserMinus, HandHelping } from 'lucide-react';

import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Avatar } from '../ui/Avatar';
import { Spinner } from '../ui/Spinner';
import { TicketStatusBadge } from './TicketStatusBadge';
import { getDisplayStatus } from './ticketStatusDisplay';
import { TicketCategoryBadge } from './TicketCategoryBadge';
import { TicketTimeline } from './TicketTimeline';
import { TicketNotes } from './TicketNotes';
import { TicketAttachments } from './TicketAttachments';
import { TicketAssignModal } from './TicketAssignModal';
import { TicketReferentModal } from './TicketReferentModal';
import { TicketDelegateModal } from './TicketDelegateModal';
import { TicketConvertModal } from '../dossiers/TicketConvertModal';
import { TicketReplyModal } from './TicketReplyModal';
import { TicketCorrespondence } from './TicketCorrespondence';
import { REPLY_TEMPLATES, canReplyToCandidate } from '../../services/tickets/replyTemplates';
import type { ReplyTemplateKey } from '../../services/tickets/replyTemplates';
import { Briefcase } from 'lucide-react';
import {
  useTicket,
  useMarkTicketAsSeen,
  useUpdateTicketStatus,
  useClaimTicket,
  useArchiveTicket,
  useDeleteTicket,
  useUpdateTicketCategoryOnTicket,
  useUpdateTicketClientFields,
  useAssignTicket,
  useUnassignTicket,
} from '../../hooks/queries/useTickets';
import { useTicketCategories } from '../../hooks/queries/useTicketCategories';
import { useCanAccessFeature } from '../../hooks/usePermissions';
import { useAuthStore } from '../../store/useAuthStore';
import { CollaboratorLink } from '../collaborators/CollaboratorLink';
import {
  CLIENT_FOLLOW_UP_OPTIONS,
  normalizeClientFollowUp,
} from '../../services/tickets/clientFollowUp';

const TICKET_STATUSES = [
  { value: 'new', label: 'Nouveau' },
  { value: 'in_progress', label: 'Pris en charge' },
] as const;

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: ticket, isLoading, error } = useTicket(id);
  usePublishPageCrumb(ticket?.sender_name);
  const markAsSeen = useMarkTicketAsSeen();
  const updateStatus = useUpdateTicketStatus();
  const claim = useClaimTicket();
  const assignTicket = useAssignTicket();
  const unassignTicket = useUnassignTicket();
  const archiveTicket = useArchiveTicket();
  const deleteTicket = useDeleteTicket();
  const updateCategory = useUpdateTicketCategoryOnTicket();
  const updateClientFields = useUpdateTicketClientFields();
  const { data: categories } = useTicketCategories(false);

  const currentCollaborator = useAuthStore((s) => s.collaborator);
  const currentCollaboratorId = currentCollaborator?.id;
  const canAssign = useCanAccessFeature('tickets_assign');
  const canClose = useCanAccessFeature('tickets_close');
  const canRespond = useCanAccessFeature('tickets_respond');
  const canClaim = useCanAccessFeature('tickets_claim');
  const canArchive = useCanAccessFeature('tickets_archive');
  const canDelete = useCanAccessFeature('tickets_delete');
  const canEditCategory = useCanAccessFeature('tickets_edit_category');
  const canAttach = useCanAccessFeature('tickets_attach');
  const canDelegate = useCanAccessFeature('tickets_delegate');
  const canCreateDossier = useCanAccessFeature('dossiers_create');
  const canReply = useCanAccessFeature('tickets_reply');

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [referentModalOpen, setReferentModalOpen] = useState(false);
  const [delegateModalOpen, setDelegateModalOpen] = useState(false);
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [replyTemplateKey, setReplyTemplateKey] = useState<ReplyTemplateKey | null>(null);

  // Mark as seen on mount
  useEffect(() => {
    if (id) {
      markAsSeen.mutate(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <Spinner />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="main-container-gdm space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/tickets')}>
          <ArrowLeft className="w-4 h-4" />
          Retour
        </Button>
        <Card>
          <p className="text-neutral-500 text-center py-8">
            Demande introuvable ou accès non autorisé.
          </p>
        </Card>
      </div>
    );
  }

  const createdAgo = formatDistanceToNow(new Date(ticket.created_at), {
    addSuffix: true,
    locale: fr,
  });
  const createdFull = format(new Date(ticket.created_at), "d MMMM yyyy 'à' HH:mm", {
    locale: fr,
  });

  const isAssignedToMe =
    !!currentCollaboratorId && ticket.assigned_to === currentCollaboratorId;
  const isReferentOfThis =
    !!currentCollaboratorId && ticket.referent_id === currentCollaboratorId;
  const isRoutedToMeUntreated = isReferentOfThis && !ticket.assigned_to;
  const canShowClaimCta = canClaim && isAssignedToMe && ticket.status === 'new';

  const showReplyCard =
    canReply &&
    canReplyToCandidate(
      currentCollaborator ? { id: currentCollaborator.id, role: currentCollaborator.role } : null,
      {
        assigned_to: ticket.assigned_to,
        referent_id: ticket.referent_id,
        sender_email: ticket.sender_email,
        category_slug: ticket.category?.slug ?? null,
      },
    );

  return (
    <div className="main-container-gdm space-y-6">
      {/* Back button */}
      <button
        onClick={() => navigate('/tickets')}
        className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour aux demandes
      </button>

      {/* Bloc titre : surtitre, nom, statut, coordonnées */}
      <div className="!mt-3">
        <p className="text-[11px] uppercase tracking-wider text-cime-text-muted">
          Demande · #{ticket.id.slice(0, 8).toUpperCase()}
        </p>
        <div className="mt-0.5 flex flex-wrap items-center gap-3">
          <h1
            className="text-[24px] font-semibold leading-tight text-cime-text-primary"
            style={{ fontFamily: 'var(--cime-font-heading)' }}
          >
            {ticket.sender_name ?? 'Expéditeur inconnu'}
          </h1>
          <TicketStatusBadge status={getDisplayStatus(ticket)} />
        </div>
        <p className="mt-1 text-[13px] text-neutral-400">
          Reçu le <span className="text-cime-text-primary">{createdFull}</span> · {createdAgo} · via formulaire site web
        </p>
        <div className="mt-1.5 flex flex-wrap gap-4">
          {ticket.sender_email && (
            <a
              href={`mailto:${ticket.sender_email}`}
              className="flex items-center gap-1.5 text-sm text-neutral-600 hover:text-cime-primary transition-colors"
            >
              <Mail className="w-4 h-4" aria-hidden />
              {ticket.sender_email}
            </a>
          )}
          {ticket.sender_phone && (
            <a
              href={`tel:${ticket.sender_phone}`}
              className="flex items-center gap-1.5 text-sm text-neutral-600 hover:text-cime-primary transition-colors"
            >
              <Phone className="w-4 h-4" aria-hidden />
              {ticket.sender_phone}
            </a>
          )}
        </div>
      </div>

      {/* "Prendre en charge" CTA — visible uniquement pour l'assigné, sur ticket 'new' */}
      {canShowClaimCta && (
        <Card>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm font-semibold text-cime-text-primary">
                Cette demande vous a été assignée
              </p>
              <p className="text-xs text-neutral-500 mt-0.5">
                Confirmez la prise en charge pour passer la demande en « En cours ».
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => id && claim.mutate(id)}
              disabled={claim.isPending}
              isLoading={claim.isPending}
            >
              <HandHelping className="w-4 h-4" />
              Prendre en charge
            </Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_330px]">
        {/* ── Main column ── */}
        <div className="min-w-0 space-y-4">
          {/* Message */}
          <Card headerClassName="px-[18px] py-2.5" header={<span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Message</span>}>
            <p className="text-sm text-neutral-700 whitespace-pre-wrap leading-relaxed">
              {ticket.message}
            </p>
          </Card>

          {/* Score candidature (recrutement) — étoiles Nova/Gemini */}
          {ticket.recruitment_stars != null && (
            <Card
              headerClassName="px-[18px] py-2.5"
              header={
                <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Évaluation candidature</span>
              }
            >
              <div className="flex items-center gap-2">
                <span className="flex" aria-label={`${ticket.recruitment_stars} sur 5`}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <svg
                      key={n}
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill={n <= ticket.recruitment_stars! ? '#c9a84c' : 'none'}
                      stroke="#c9a84c"
                      strokeWidth="1.5"
                      aria-hidden="true"
                    >
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  ))}
                </span>
                <span className="text-sm font-medium text-neutral-600">
                  {ticket.recruitment_stars}/5
                </span>
              </div>
            </Card>
          )}

          {/* AI Summary — toujours visible */}
          <Card headerClassName="px-[18px] py-2.5" header={<span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Résumé IA</span>}>
            {ticket.ai_summary ? (
              <>
                <p className="text-sm text-neutral-600 italic">{ticket.ai_summary}</p>
                {ticket.ai_confidence !== null && (
                  <p className="text-xs text-neutral-400 mt-1">
                    Confiance : {Math.round(ticket.ai_confidence * 100)}%
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-neutral-400 italic">
                Analyse IA non disponible — la classification automatique n'a pas pu être générée pour cette demande.
              </p>
            )}
          </Card>

          {/* Notes internes (fil) */}
          <Card
            headerClassName="px-[18px] py-2.5"
            header={<span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Notes internes</span>}
          >
            <TicketNotes ticketId={ticket.id} canWrite={canRespond} />
          </Card>

          {/* Correspondance envoyée au candidat — distincte des notes internes ci-dessus */}
          <TicketCorrespondence ticketId={ticket.id} />

          {/* Pièces jointes */}
          <Card
            headerClassName="px-[18px] py-2.5"
            header={<span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Pièces jointes</span>}
          >
            <TicketAttachments
              ticketId={ticket.id}
              canUpload={canAttach}
              canDelete={canAttach}
            />
          </Card>

          {/* Activity timeline */}
          <Card
            headerClassName="px-[18px] py-2.5"
            header={<span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Historique</span>}
          >
            <TicketTimeline ticketId={ticket.id} />
          </Card>
        </div>

        {/* ── Sidebar ── */}
        <div className="min-w-0 space-y-4 [&_button]:text-[13px] [&_button_svg]:h-3.5 [&_button_svg]:w-3.5">
          {/* Actions */}
          <Card headerClassName="px-[18px] py-2.5" header={<span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Actions</span>}>
            <div className="space-y-3">
              {/* Référent + Avocat en charge */}
              {canAssign && (
                <>
                  {/* Widget A — Avocat référent */}
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">
                      Avocat référent
                    </p>
                    {ticket.referent ? (
                      <CollaboratorLink collaboratorId={ticket.referent.id} title={`Voir le profil de ${ticket.referent.full_name}`}>
                        <div className="flex items-center gap-2">
                          <Avatar
                            name={ticket.referent.full_name}
                            src={ticket.referent.avatar_url ?? undefined}
                            size="sm"
                          />
                          <span className="text-sm font-medium text-neutral-700">
                            {ticket.referent.full_name}
                          </span>
                        </div>
                      </CollaboratorLink>
                    ) : (
                      <p className="text-sm text-neutral-400 italic">Aucun référent</p>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      className="w-full mt-2"
                      onClick={() => setReferentModalOpen(true)}
                    >
                      <User className="w-4 h-4" />
                      {ticket.referent ? 'Modifier' : 'Désigner'}
                    </Button>
                  </div>

                  <div className="border-t border-neutral-100" />

                  {/* Widget B — Avocat en charge */}
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">
                      Avocat en charge
                    </p>
                    {ticket.assignee ? (
                      <CollaboratorLink collaboratorId={ticket.assignee.id} title={`Voir le profil de ${ticket.assignee.full_name}`}>
                        <div className="flex items-center gap-2">
                          <Avatar
                            name={ticket.assignee.full_name}
                            src={ticket.assignee.avatar_url ?? undefined}
                            size="sm"
                          />
                          <span className="text-sm font-medium text-neutral-700">
                            {ticket.assignee.full_name}
                          </span>
                        </div>
                      </CollaboratorLink>
                    ) : (
                      <p className="text-sm text-neutral-400 italic">Non assigné</p>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      className="w-full mt-2"
                      onClick={() => setAssignModalOpen(true)}
                    >
                      <User className="w-4 h-4" />
                      {ticket.assignee ? 'Réassigner' : 'Assigner'}
                    </Button>
                    {ticket.assignee && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full"
                        disabled={unassignTicket.isPending}
                        onClick={() => {
                          if (
                            !window.confirm(
                              `Retirer ${ticket.assignee!.full_name} de cette demande ? Elle repassera en « nouvelle », sans avocat en charge.`,
                            )
                          )
                            return;
                          unassignTicket.mutate(ticket.id);
                        }}
                      >
                        <UserMinus className="w-4 h-4" />
                        Retirer l'avocat en charge
                      </Button>
                    )}
                  </div>
                </>
              )}

              {/* Référent : demande routée chez moi, pas encore prise en charge */}
              {canDelegate && isRoutedToMeUntreated && (
                <div className="space-y-1.5">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    onClick={() => setDelegateModalOpen(true)}
                  >
                    Déléguer à un collaborateur
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    disabled={assignTicket.isPending}
                    onClick={() =>
                      currentCollaboratorId &&
                      assignTicket.mutate({
                        ticketId: ticket.id,
                        collaboratorId: currentCollaboratorId,
                      })
                    }
                  >
                    Prendre en charge moi-même
                  </Button>
                </div>
              )}

              {/* Delegate — visible for lawyers assigned to this ticket */}
              {canDelegate && isAssignedToMe && (
                <div className="space-y-1.5">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    onClick={() => setDelegateModalOpen(true)}
                  >
                    Déléguer à un collaborateur
                  </Button>
                </div>
              )}

              {/* Convert to dossier — A-14 */}
              {canCreateDossier && !ticket.converted_to_dossier_id && (
                <div className="space-y-1.5">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    onClick={() => setConvertModalOpen(true)}
                  >
                    <Briefcase className="w-4 h-4 mr-1.5" />
                    Convertir en dossier
                  </Button>
                </div>
              )}
              {ticket.converted_to_dossier_id && (
                <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
                  ✓ Converti en{' '}
                  <button
                    type="button"
                    onClick={() => navigate(`/dossiers/${ticket.converted_to_dossier_id}`)}
                    className="underline font-medium"
                  >
                    dossier
                  </button>
                </div>
              )}

              {/* Répondre au candidat — demandes de recrutement uniquement */}
              {showReplyCard && (
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-neutral-500">Répondre au candidat</p>
                  {REPLY_TEMPLATES.map((tpl) => (
                    <Button
                      key={tpl.key}
                      variant={tpl.tone === 'danger' ? 'danger' : 'secondary'}
                      size="sm"
                      className="w-full"
                      onClick={() => setReplyTemplateKey(tpl.key)}
                    >
                      {tpl.label}
                    </Button>
                  ))}
                </div>
              )}

              {/* Status */}
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">
                  Statut
                </p>
                <select
                  value={ticket.status}
                  onChange={(e) => {
                    if (id) {
                      updateStatus.mutate({ ticketId: id, status: e.target.value });
                    }
                  }}
                  disabled={!canClose}
                  className="w-full text-sm border border-neutral-300 rounded-cime px-2 py-1.5 text-neutral-700 focus:outline-none focus:ring-2 focus:ring-cime-primary disabled:opacity-50 disabled:cursor-not-allowed bg-white"
                >
                  {TICKET_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Archive */}
              {canArchive && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-neutral-500 hover:text-neutral-700 mt-1"
                  disabled={archiveTicket.isPending}
                  isLoading={archiveTicket.isPending}
                  onClick={() => {
                    if (id) {
                      archiveTicket.mutate(id, { onSuccess: () => navigate('/tickets') });
                    }
                  }}
                >
                  <Archive className="w-4 h-4" />
                  Archiver
                </Button>
              )}

              {/* Hard delete */}
              {canDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 mt-1"
                  disabled={deleteTicket.isPending}
                  isLoading={deleteTicket.isPending}
                  onClick={() => {
                    if (!id) return;
                    if (
                      window.confirm(
                        'Supprimer définitivement cette demande ? Cette action est irréversible et efface aussi les pièces jointes.',
                      )
                    ) {
                      deleteTicket.mutate(id, { onSuccess: () => navigate('/tickets') });
                    }
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                  Supprimer définitivement
                </Button>
              )}
            </div>
          </Card>

          {/* Info */}
          <Card headerClassName="px-[18px] py-2.5" header={<span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Infos</span>}>
            <dl className="space-y-2.5 text-sm">
              <div>
                <dt className="text-xs font-medium text-neutral-400 uppercase tracking-wide mb-0.5">
                  Catégorie
                </dt>
                <dd>
                  {canEditCategory ? (
                    <select
                      value={ticket.category_id ?? ''}
                      onChange={(e) => {
                        if (id) {
                          updateCategory.mutate({
                            ticketId: id,
                            categoryId: e.target.value || null,
                          });
                        }
                      }}
                      disabled={updateCategory.isPending}
                      className="w-full text-sm border border-neutral-300 rounded-cime px-2 py-1.5 text-neutral-700 focus:outline-none focus:ring-2 focus:ring-cime-primary disabled:opacity-50 bg-white"
                    >
                      <option value="">Non classé</option>
                      {(categories ?? []).map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <TicketCategoryBadge category={ticket.category} />
                  )}
                </dd>
              </div>
              {ticket.referent && (
                <div>
                  <dt className="text-xs font-medium text-neutral-400 uppercase tracking-wide mb-0.5">
                    Référent
                  </dt>
                  <dd className="flex items-center gap-2">
                    <CollaboratorLink collaboratorId={ticket.referent.id} title={`Voir le profil de ${ticket.referent.full_name}`}>
                      <div className="flex items-center gap-2">
                        <Avatar
                          name={ticket.referent.full_name}
                          src={ticket.referent.avatar_url ?? undefined}
                          size="sm"
                        />
                        <span className="text-sm text-neutral-700">{ticket.referent.full_name}</span>
                      </div>
                    </CollaboratorLink>
                  </dd>
                </div>
              )}

              <div>
                <dt className="text-xs font-medium text-neutral-400 uppercase tracking-wide mb-0.5">
                  Identifiant
                </dt>
                <dd className="text-neutral-600 tabular-nums text-xs">
                  #{ticket.id.slice(0, 8)}
                </dd>
              </div>
              {ticket.submission_id && (
                <div>
                  <dt className="text-xs font-medium text-neutral-400 uppercase tracking-wide mb-0.5">
                    Source
                  </dt>
                  <dd className="flex items-center gap-1 text-neutral-600">
                    <Globe className="w-3 h-3" aria-hidden />
                    Formulaire web
                  </dd>
                </div>
              )}
              {ticket.closed_at && (
                <div>
                  <dt className="text-xs font-medium text-neutral-400 uppercase tracking-wide mb-0.5">
                    Clos le
                  </dt>
                  <dd className="text-neutral-600">
                    {format(new Date(ticket.closed_at), "d MMM yyyy", { locale: fr })}
                  </dd>
                </div>
              )}
            </dl>
          </Card>
          {/* Client follow-up */}
          <Card headerClassName="px-[18px] py-2.5" header={<span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Suivi client</span>}>
            <div className="space-y-4">
              {/* État client */}
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-neutral-400 uppercase tracking-wide">
                  État
                </p>
                <select
                  value={normalizeClientFollowUp(ticket.client_status) ?? ''}
                  onChange={(e) => {
                    if (id) {
                      updateClientFields.mutate({
                        ticketId: id,
                        fields: { client_status: e.target.value || null },
                      });
                    }
                  }}
                  disabled={updateClientFields.isPending}
                  className="w-full text-sm border border-neutral-300 rounded-cime px-2 py-1.5 text-neutral-700 focus:outline-none focus:ring-2 focus:ring-cime-primary disabled:opacity-50 bg-white"
                >
                  {CLIENT_FOLLOW_UP_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </Card>

          {/* Archives link */}
          <Card>
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-neutral-500"
              onClick={() => navigate('/tickets/archives')}
            >
              <Archive className="w-4 h-4" />
              Voir les archives
            </Button>
          </Card>
        </div>
      </div>

      {/* Assign modal */}
      {assignModalOpen && (
        <TicketAssignModal
          ticket={ticket}
          open={assignModalOpen}
          onOpenChange={setAssignModalOpen}
        />
      )}

      {/* Referent modal */}
      {referentModalOpen && (
        <TicketReferentModal
          ticket={ticket}
          open={referentModalOpen}
          onOpenChange={setReferentModalOpen}
        />
      )}

      {/* Delegate modal */}
      {delegateModalOpen && currentCollaboratorId && (
        <TicketDelegateModal
          ticket={ticket}
          currentCollaboratorId={currentCollaboratorId}
          open={delegateModalOpen}
          onOpenChange={setDelegateModalOpen}
        />
      )}

      {/* Convert to dossier modal — A-14 */}
      {convertModalOpen && (
        <TicketConvertModal
          ticketId={ticket.id}
          defaultTitle={
            (ticket.message?.match(/^Sujet\s*:\s*(.+?)(?:\n|$)/m)?.[1]?.trim())
            || ticket.sender_name
            || 'Nouveau dossier'
          }
          defaultClientName={ticket.sender_name ?? undefined}
          onClose={() => setConvertModalOpen(false)}
        />
      )}

      {/* Reply modal */}
      {replyTemplateKey && currentCollaborator && (
        <TicketReplyModal
          ticket={ticket}
          templateKey={replyTemplateKey}
          lawyerName={currentCollaborator.full_name}
          lawyerEmail={currentCollaborator.email}
          open={replyTemplateKey !== null}
          onOpenChange={(open) => {
            if (!open) setReplyTemplateKey(null);
          }}
        />
      )}
    </div>
  );
}
